import { useState, useCallback } from "react";
// @ts-expect-error — canvas-confetti has no type declarations
import confetti from "canvas-confetti";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import { formatMonthDay } from "@/lib/i18n";
import type { DealSummary, PipelineStage } from "@/hooks/use-deals";
import { usePipelineAI } from "@/hooks/use-crm-pipeline-ai";
import { changeDealStage } from "@/hooks/use-deals";
import { DealAiIndicators } from "@/components/crm/deal-ai-indicators";
import { UserRound, X } from "lucide-react";

interface KanbanBoardProps {
  stages: PipelineStage[];
  deals: DealSummary[];
  pipelineId: string;
  ownerLabelById?: ReadonlyMap<string, string>;
  onDealClick: (dealId: string) => void;
  onRefresh: () => void;
}

// ---------------------------------------------------------------------------
// Brand colors for confetti
// ---------------------------------------------------------------------------
const BRAND_COLORS = ["#1040CC", "#7C3AED", "#F05E0A"];

function fireConfetti() {
  const count = 40;
  const defaults = { origin: { y: 0.7 }, colors: BRAND_COLORS };
  confetti({ ...defaults, particleCount: count, spread: 60, startVelocity: 30, ticks: 80 });
  confetti({ ...defaults, particleCount: Math.floor(count * 0.5), spread: 100, startVelocity: 20, ticks: 60, scalar: 0.8 });
}

// ---------------------------------------------------------------------------
// Kanban Board
// ---------------------------------------------------------------------------

export function KanbanBoard({ stages, deals, pipelineId, ownerLabelById, onDealClick, onRefresh }: KanbanBoardProps) {
  const { data: pipelineSummary } = usePipelineAI(pipelineId);
  const [draggedDealId, setDraggedDealId] = useState<string | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);
  // Optimistic: track pending stage moves
  const [pendingMoves, setPendingMoves] = useState<Map<string, string>>(new Map());
  // "Why lost?" modal state
  const [lostDealId, setLostDealId] = useState<string | null>(null);
  const [lostReason, setLostReason] = useState("");
  const [lostCategory, setLostCategory] = useState("");

  // Apply optimistic moves to deals
  const effectiveDeals = deals.map((d) => {
    const pendingStage = pendingMoves.get(d.id);
    return pendingStage ? { ...d, stageId: pendingStage } : d;
  });

  const dealsByStage = new Map<string, DealSummary[]>();
  const metricsByStage = new Map(
    (pipelineSummary?.stages ?? []).map((stage) => [stage.stageId, stage]),
  );
  for (const stage of stages) {
    dealsByStage.set(stage.id, []);
  }
  for (const deal of effectiveDeals) {
    const stageDeals = dealsByStage.get(deal.stageId);
    if (stageDeals) stageDeals.push(deal);
  }

  const handleDragStart = (dealId: string) => {
    setDraggedDealId(dealId);
  };

  const handleDragOver = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    setDragOverStageId(stageId);
  };

  const executeDrop = useCallback(async (dealId: string, toStageId: string, reason?: string) => {
    // Optimistic move — stays until refetch replaces data
    setPendingMoves((prev) => new Map(prev).set(dealId, toStageId));

    try {
      await changeDealStage(dealId, toStageId, reason);
      // Refetch will replace deals array, making pendingMoves stale but harmless
      await onRefresh();
    } catch {
      // Revert optimistic move on failure — card snaps back
    }
    // Always clean up pending state after refetch or revert
    setPendingMoves((prev) => {
      const next = new Map(prev);
      next.delete(dealId);
      return next;
    });
  }, [onRefresh]);

  const handleDrop = async (stageId: string) => {
    if (!draggedDealId) return;
    const deal = deals.find((d) => d.id === draggedDealId);
    if (!deal || deal.stageId === stageId) {
      setDraggedDealId(null);
      setDragOverStageId(null);
      return;
    }

    const targetStage = stages.find((s) => s.id === stageId);

    setDraggedDealId(null);
    setDragOverStageId(null);

    // Won stage → confetti + celebration
    if (targetStage?.isWinStage) {
      fireConfetti();
      await executeDrop(deal.id, stageId);
      // C37: Could add celebration toast with deal.value count-up here
      // when toast system is wired. For now, confetti is the celebration.
      return;
    }

    // Lost stage → "Why lost?" prompt
    if (targetStage?.isLossStage) {
      setLostDealId(deal.id);
      setLostReason("");
      setLostCategory("");
      // Optimistic move to Lost while modal is open
      setPendingMoves((prev) => new Map(prev).set(deal.id, stageId));
      return;
    }

    // Normal stage move
    await executeDrop(deal.id, stageId);
  };

  const handleLostSubmit = async () => {
    if (!lostDealId) return;
    const lostStage = stages.find((s) => s.isLossStage);
    const reason = [lostCategory, lostReason].filter(Boolean).join(": ").trim() || undefined;
    if (lostStage) {
      await executeDrop(lostDealId, lostStage.id, reason);
    }
    setLostDealId(null);
    setLostReason("");
    setLostCategory("");
  };

  const handleLostCancel = () => {
    // Revert optimistic move
    if (lostDealId) {
      setPendingMoves((prev) => {
        const next = new Map(prev);
        next.delete(lostDealId);
        return next;
      });
    }
    setLostDealId(null);
    setLostReason("");
    setLostCategory("");
  };

  const lostDeal = lostDealId ? deals.find((d) => d.id === lostDealId) : null;

  return (
    <>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) => {
          const stageDeals = dealsByStage.get(stage.id) ?? [];
          const stageTotal = stageDeals.reduce((sum, d) => sum + (d.value ?? 0), 0);
          const stageMetrics = metricsByStage.get(stage.id);
          const weightedValue = stageMetrics?.weightedValue ?? 0;
          const avgDaysInStage = stageMetrics?.avgDaysInStage ?? 0;

          return (
            <div
              key={stage.id}
              className={cn(
                "flex w-72 shrink-0 flex-col rounded-lg border border-border bg-muted/30 transition-all duration-200",
                dragOverStageId === stage.id && "ring-2 ring-[var(--color-primary)]/50 border-[var(--color-primary)]/30",
              )}
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDragLeave={() => setDragOverStageId(null)}
              onDrop={() => handleDrop(stage.id)}
            >
              {/* Column header */}
              <div className="border-b border-border px-3 py-2.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <div
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: stage.color }}
                    />
                    <h3 className="truncate text-sm font-semibold text-foreground">{stage.name}</h3>
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {stageDeals.length}
                    </span>
                  </div>
                  {stageTotal > 0 && (
                    <span className="text-xs font-medium text-muted-foreground" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatCurrency(stageTotal, stageDeals[0]?.currency ?? "EUR")}
                    </span>
                  )}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                  <div className="rounded-md border border-border/70 bg-background/80 px-2 py-1">
                    <span className="block uppercase tracking-wide text-[10px] text-muted-foreground/80">Weighted</span>
                    <span className="font-semibold text-foreground" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {weightedValue > 0
                        ? formatCurrency(weightedValue, stageDeals[0]?.currency ?? "EUR")
                        : "—"}
                    </span>
                  </div>
                  <div className="rounded-md border border-border/70 bg-background/80 px-2 py-1">
                    <span className="block uppercase tracking-wide text-[10px] text-muted-foreground/80">Velocity</span>
                    <span className="font-semibold text-foreground" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {avgDaysInStage > 0 ? `${avgDaysInStage}d avg` : "—"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Cards */}
              <div className="flex-1 space-y-2 p-2 min-h-[120px]">
                {stageDeals.map((deal) => (
                  <DealCard
                    key={deal.id}
                    deal={deal}
                    isDragging={draggedDealId === deal.id}
                    ownerLabelById={ownerLabelById}
                    onClick={() => onDealClick(deal.id)}
                    onDragStart={() => handleDragStart(deal.id)}
                  />
                ))}
                {stageDeals.length === 0 && (
                  <p className="py-8 text-center text-xs text-muted-foreground">
                    No deals
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* "Why lost?" modal */}
      {lostDealId && lostDeal && (
        <>
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[4px]" onClick={handleLostCancel} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="w-full max-w-md rounded-xl border border-border bg-background shadow-xl animate-in zoom-in-95 fade-in duration-250"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-border px-6 py-4">
                <h2 className="text-lg font-semibold text-foreground">Why was this deal lost?</h2>
                <button type="button" onClick={handleLostCancel} className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" aria-label="Close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-4 px-6 py-4">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{lostDeal.title}</span>
                  {lostDeal.value != null && ` — ${formatCurrency(lostDeal.value, lostDeal.currency)}`}
                </p>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Reason</label>
                  <select
                    value={lostCategory}
                    onChange={(e) => setLostCategory(e.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)]"
                  >
                    <option value="">Select reason...</option>
                    <option value="price">Price</option>
                    <option value="competitor">Competitor</option>
                    <option value="timing">Timing</option>
                    <option value="no_response">No Response</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Notes (optional)</label>
                  <textarea
                    value={lostReason}
                    onChange={(e) => setLostReason(e.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)]"
                    rows={3}
                    placeholder="What happened?"
                    autoFocus
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
                <button
                  type="button"
                  onClick={handleLostCancel}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleLostSubmit}
                  className="btn-press rounded-lg px-5 py-2 text-sm font-medium text-white bg-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/90 transition-colors"
                >
                  Mark as Lost
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Deal Card — enhanced design
// ---------------------------------------------------------------------------

function DealCard({
  deal,
  isDragging,
  ownerLabelById,
  onClick,
  onDragStart,
}: {
  deal: DealSummary;
  isDragging: boolean;
  ownerLabelById?: ReadonlyMap<string, string>;
  onClick: () => void;
  onDragStart: () => void;
}) {
  const ownerLabel = formatDealOwner(deal.ownerId, ownerLabelById);

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className={cn(
        "stagger-item card-hover cursor-pointer rounded-md border border-border bg-card p-3 transition-all duration-200",
        isDragging && "scale-[1.03] shadow-lg opacity-70 rotate-[1deg]",
        !isDragging && "shadow-sm hover:shadow-md hover:-translate-y-[1px]",
      )}
    >
      <p className="text-sm font-semibold text-foreground leading-tight">{deal.title}</p>
      {deal.value != null && (
        <p className="mt-1 text-sm font-bold text-[var(--color-primary)]" style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatCurrency(deal.value, deal.currency)}
        </p>
      )}
      <p className="mt-1 inline-flex max-w-full items-center gap-1 text-xs text-muted-foreground">
        <UserRound className="h-3 w-3 shrink-0" />
        <span className="truncate">Owner: {ownerLabel ?? "Unassigned"}</span>
      </p>
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        {deal.expectedCloseDate && (
          <span>Close: {formatMonthDay(deal.expectedCloseDate)}</span>
        )}
        {deal.score > 0 && <span>Score: {deal.score}</span>}
      </div>
      <DealAiIndicators deal={deal} />
    </div>
  );
}

function formatDealOwner(ownerId: string | null | undefined, ownerLabelById?: ReadonlyMap<string, string>): string | null {
  const normalizedOwnerId = ownerId?.trim();
  if (!normalizedOwnerId) {
    return null;
  }
  return ownerLabelById?.get(normalizedOwnerId) ?? normalizedOwnerId;
}
